import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'i18next';
import { chatbotMessageStyles } from './ChatbotMessage.styles';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { provide } from '@lit/context';
import '../chatbot-escalation-pill/ChatbotEscalationPill';
import '../chatbot-message-rating/ChatbotMessageRating';
import '../chatbot-conversation-rating/ChatbotConversationRating';
import { chatbotMessageContext, type ChatbotMessageContext } from './ChatbotMessageContext';
import { SuperElement } from '@atlas/shared/client/ui/wc/internals/super/SuperElement';
import { isMobileDevice } from '@atlas/shared/client/ui/core/utils';
import { transformTextToLinks } from '../../utils/transform-text-to-links';

@customElement('lr-chatbot-message')
export class ChatbotMessage extends SuperElement {
    @property({ type: String })
    apiUrl = '';
    @property({ type: String })
    conversationId = '';
    @property({ type: String })
    messageId = '';
    @property({ type: Number })
    score?: number;
    @property({ type: Boolean })
    showEscalation = false;
    @property({ type: Boolean })
    showMessageRating = false;
    @property({ type: Boolean })
    showConversationRating = false;

    @provide({ context: chatbotMessageContext })
    messageContext: ChatbotMessageContext = {};

    constructor() {
        super();
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.messageContext = {
            conversationId: this.conversationId,
            score: this.score,
            messageId: this.messageId,
            apiUrl: this.apiUrl
        };
    }

    static styles = [SuperElement.styles, chatbotMessageStyles];

    renderElement() {
        return html` <slot></slot>

            ${this.showEscalation
                ? html`<div class="button-container">
                      ${unsafeHTML(
                          t('chatbot:escalation.suggestions', {
                              context: isMobileDevice() ? 'mobile' : ''
                          })
                      )}
                  </div>`
                : nothing}
            ${this.showMessageRating
                ? html`<lr-chatbot-message-rating .score=${this.score}></lr-chatbot-message-rating>`
                : nothing}
            ${this.showConversationRating
                ? html`<lr-chatbot-conversation-rating></lr-chatbot-conversation-rating>`
                : nothing}`;
    }
}

type ChatbotMessageProps = {
    [Property in keyof ChatbotMessage as ChatbotMessage[Property] extends (...args: unknown[]) => unknown
        ? never
        : Property]?: ChatbotMessage[Property];
} & { slot: string };

/**
 * Escape HTML attribute values to prevent attribute injection
 */
function escapeHtmlAttribute(value: string | number | undefined): string {
    if (value === undefined || value === null) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

export const createChatbotMessageHtml = (props: ChatbotMessageProps) => {
    const messageWithLineBreaks = props.slot.replace(/(?:\r\n|\r|\n)/g, '<br/>');
    const messageWithLinks = transformTextToLinks(messageWithLineBreaks);

    // Escape all attribute values to prevent attribute injection
    const sanitizedApiUrl = escapeHtmlAttribute(props.apiUrl);
    const sanitizedConversationId = escapeHtmlAttribute(props.conversationId);
    const sanitizedMessageId = escapeHtmlAttribute(props.messageId);
    const sanitizedScore = escapeHtmlAttribute(props.score);

    return `<lr-chatbot-message
        ${props.apiUrl ? `apiUrl="${sanitizedApiUrl}"` : ''}
        ${props.conversationId ? `conversationId="${sanitizedConversationId}"` : ''}
        ${props.messageId ? `messageId="${sanitizedMessageId}"` : ''}
        ${props.score !== undefined && props.score !== null ? `score="${sanitizedScore}"` : ''}
        ${props.showEscalation ? 'showEscalation' : ''}
        ${props.showMessageRating ? 'showMessageRating' : ''}
        ${props.showConversationRating ? 'showConversationRating' : ''}
      >
     ${messageWithLinks}
    </lr-chatbot-message>`;
};
